import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Axios from "../../_services/caller.service";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./Statistics.module.scss";
import {
  FiPlay,
  FiFileText,
  FiVolume2,
  FiActivity,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
  FiUsers,
  FiMonitor,
} from "react-icons/fi";

import GlobalSelectMetrics from "./charts/GlobalSelectMetrics";
import GlobalComparisonMetrics from "./charts/GloblaComparisonMetrics";
import metricsConfig from "./metricsConfig";

function Statistics() {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState(new Date(2024, 0, 1));
  const [endDate, setEndDate] = useState(new Date());
  const [allUsers, setAllUsers] = useState([]);
  const [usersMap, setUsersMap] = useState({});

  const [allCabines, setAllCabines] = useState([]);
  const [cabinesMap, setCabinesMap] = useState({});

  const [selectedUsers, setSelectedUsers] = useState([]); // Ajout de l'état pour l'utilisateur sélectionné
  const [selectedCabines, setSelectedCabines] = useState([]); // Ajout de l'état pour la cabine sélectionnée
  const [linkedUsers, setLinkedUsers] = useState([]);
  const [timeInterval, setTimeInterval] = useState("monthly");
  const [defaultMetrics, setDefaultMetrics] = useState(["HSStarted"]);
  const defaultSelectedMetrics = defaultMetrics && defaultMetrics.map((metric) => ({
    value: metric,
    label: metricsConfig[metric].title,
  }));

  const [selectedMetrics, setSelectedMetrics] = useState(
    defaultSelectedMetrics
  );
  // data and data formatted
  const [data, setData] = useState([]);
  const [detailedData, setDetailedData] = useState([]);
  //Comparaison user or cabines
  const [comparisonMode, setComparisonMode] = useState("none");
  const [comparisonData, setComparisonData] = useState([]);
  const [triggerFetch, setTriggerFetch] = useState(false);

  const [error, setError] = useState("");

  // Chargement initial des utilisateurs
  useEffect(() => {
    Axios.get(`/getAllUserForSuperAdmin`)
      .then((response) => {
        const usersOptions = [
          { value: null, label: t("stats.no_user") }, // Option par défaut
          ...response.data.data.map((user) => ({
            value: user.ID,
            label: `${user.user_login} - ${user.display_name} - ${user.ID}`,
          })),
        ];

        setAllUsers(usersOptions);
        const userMap = response.data.data.reduce((acc, user) => {
          acc[user.ID] = {
            displayName: user.display_name,
            userLogin: user.user_login,
          };
          return acc;
        }, {});

        // Mise à jour de l'état avec userMap
        setUsersMap(userMap);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  // chargement initial des cabines
  useEffect(() => {
    Axios.get(`/allCabines/`)

      .then(async function (response) {
        const cabineOptions = [
          { value: null, label: t("stats.no_cabin") }, // Option par défaut
          ...response.data.data.map((cab) => ({
            value: cab.id,
            label: `${cab.cabname} - ${cab.id}`,
          })),
        ];

        setAllCabines(cabineOptions);
        const cabineMap = response.data.data.reduce((acc, cab) => {
          acc[cab.id] = cab.cabname;
          return acc;
        }, {});
        // Mise à jour de l'état avec userMap
        setCabinesMap(cabineMap);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  //STATS DATA GENERALS CUMUL

  const fetchData = () => {
    if (!startDate || !endDate) return;

    let queryStringParts = [];

    // Gérer la logique de date en fonction des dates de début et de fin
    if (startDate && endDate) {
      queryStringParts.push(`startYear=${startDate.getFullYear()}`);
      queryStringParts.push(`startMonth=${startDate.getMonth() + 1}`);
      queryStringParts.push(`startDay=${startDate.getDate()}`);
      queryStringParts.push(`endYear=${endDate.getFullYear()}`);
      queryStringParts.push(`endMonth=${endDate.getMonth() + 1}`);
      queryStringParts.push(`endDay=${endDate.getDate()}`);
    } else if (startDate) {
      // Cas où seule une date de début (ou une date unique) est sélectionnée
      queryStringParts.push(`year=${startDate.getFullYear()}`);
      queryStringParts.push(`month=${startDate.getMonth() + 1}`);
      queryStringParts.push(`day=${startDate.getDate()}`);
    }

    // Ajouter les utilisateurs et les cabines sélectionnés à la requête
    if (selectedUsers && selectedUsers.length > 0) {
      queryStringParts.push(`uids=${selectedUsers.join(",")}`);
    }

    if (selectedCabines && selectedCabines.length > 0) {
      queryStringParts.push(`cabids=${selectedCabines.join(",")}`);
    }

    // Construire la chaîne de requête finale
    let queryString = queryStringParts.join("&");

    Axios.get(`/generalStats?${queryString}`)
      .then((response) => {
        if (!response.data.error) {
          setData(response.data.data);
        } else {
          setError(response.data.message);
        }
      })
      .catch((err) => setError(err.message));
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, selectedUsers, selectedCabines]); // Déclencher fetchData sur changement de ces dépendances

  const fetchDetailedData = () => {
    if (!startDate || !endDate) return;

    let queryStringParts = [
      `startYear=${startDate.getFullYear()}`,
      `startMonth=${startDate.getMonth() + 1}`,
      `startDay=${startDate.getDate()}`,
      `endYear=${endDate.getFullYear()}`,
      `endMonth=${endDate.getMonth() + 1}`,
      `endDay=${endDate.getDate()}`,
    ];

    if (selectedUsers && selectedUsers.length > 0) {
      queryStringParts.push(`uids=${selectedUsers.join(",")}`);
    }

    if (selectedCabines && selectedCabines.length > 0) {
      queryStringParts.push(`cabids=${selectedCabines.join(",")}`);
    }

    // Utiliser une route différente qui correspond à la logique de récupération détaillée
    Axios.get(`/getDailyMetricsGeneral?${queryStringParts.join("&")}`)
      .then((response) => {
        if (!response.data.error) {
          setDetailedData(response.data.data);
        } else {
          setError(response.data.message);
        }
      })
      .catch((err) => setError(err.message));
  };

  useEffect(() => {
    fetchDetailedData();
  }, [startDate, endDate, selectedUsers, selectedCabines]); // Déclencher fetchDataDetailed sur changement de ces dépendances

  useEffect(() => {
    const fetchComparisonData = async () => {
      // Préparation des paramètres de date
      let queryString = `startYear=${startDate.getFullYear()}&startMonth=${startDate.getMonth() + 1}&startDay=${startDate.getDate()}&endYear=${endDate.getFullYear()}&endMonth=${endDate.getMonth() + 1}&endDay=${endDate.getDate()}`;
  
      // Ajout des paramètres pour les utilisateurs ou les cabines à comparer
      if (comparisonMode === "users" && selectedUsers.length > 0) {
        queryString += `&uids=${selectedUsers.join(",")}`;
      } else if (comparisonMode === "cabines" && selectedCabines.length > 0) {
        queryString += `&cabids=${selectedCabines.join(",")}`;
      } else {
        // Si aucun mode de comparaison n'est sélectionné ou aucune sélection valide n'est faite
        return;
      }
  
      try {
        const response = await Axios.get(`/compareUsersOrCabinesMetrics?${queryString}`);
        if (response.data && !response.data.error) {
          console.log("nouvelle data pour comparaison", response.data.data);
          setComparisonData(response.data.data);
        } else {
          console.error("Erreur lors de la récupération des données de comparaison", response.data.message);
        }
      } catch (error) {
        console.error("Erreur lors de la requête de comparaison", error);
      }
    };
  
    if (comparisonMode !== "none") {
      fetchComparisonData();
    }
  }, [comparisonMode, selectedUsers, selectedCabines, startDate, endDate, triggerFetch]);
  

  // GET user linked Ownerids
  const fetchLinkedAccounts = (ownerIds) => {
    // Assurez-vous que ownerIds est un tableau et convertissez-le en chaîne séparée par des virgules
    const ownerIdsParam = Array.isArray(ownerIds)
      ? ownerIds.join(",")
      : ownerIds;

    Axios.get(`/getUserInfosForStats?ownerIds=${ownerIdsParam}`)
      .then((response) => {
        if (!response.data.error && response.data.data.length > 0) {
          const linkedOptions = response.data.data.map((user) => ({
            value: user.ID,
            label: user.display_name || user.user_login, // Utilisez user.display_name ou user.user_login comme label
          }));
          setLinkedUsers(linkedOptions);
        } else {
          setLinkedUsers([]);
        }
      })
      .catch((error) => {
        console.error("Erreur lors de la récupération des comptes liés", error);
      });
  };

  const handleLinkedUserChange = (selectedOptions) => {
    const linkedUserIds = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    // Ajoutez les utilisateurs liés sélectionnés aux utilisateurs déjà sélectionnés sans duplication
    setSelectedUsers((prevUsers) => [
      ...new Set([...prevUsers, ...linkedUserIds]),
    ]);
  };

  const handleUserChange = (selectedOptions) => {
    console.log(selectedOptions);

    if (selectedOptions.length === 1 && selectedOptions[0].value === null) {
      // Nettoyer les sélections d'utilisateurs et d'utilisateurs liés
      setSelectedUsers([]);
      setLinkedUsers([]);

      return;
    }

    const userIds = selectedOptions
      ? selectedOptions.map((option) => option.value).join(",")
      : "";

    setSelectedUsers(
      selectedOptions ? selectedOptions.map((option) => option.value) : []
    );
    setLinkedUsers(
      selectedOptions ? selectedOptions.map((option) => option.value) : []
    );

    if (userIds) {
      fetchLinkedAccounts(userIds);
    }

    // Modifier l'URL pour utiliser une query string pour userIds
    const url = userIds
      ? `/getCabinesForSeveralUsers?userIds=${userIds}`
      : `/allCabines/`;

    Axios.get(url)
      .then((response) => {
        const userCabineOptions = userIds
          ? [
              { value: null, label: t("stats.no_cabin") },
              ...response.data.data.map((userCabine) => ({
                value: userCabine.id,
                label: `${userCabine.cabname} - ${userCabine.id}`,
              })),
            ]
          : [
              { value: null, label: t("stats.no_cabin") },
              ...response.data.data.map((userCabine) => ({
                value: userCabine.id,
                label: `${userCabine.cabname} - ${userCabine.id}`,
              })),
            ];

        setAllCabines(userCabineOptions);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleCabineChange = (selectedOptions) => {
    const cabineIds = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setSelectedCabines(cabineIds); // Mise à jour avec le tableau des cabineIds

    // Logique supplémentaire si nécessaire
  };

  // Gestionnaire pour "Sélectionner tous les utilisateurs"
  const handleSelectAllUsers = (e) => {
    if (e.target.checked) {
      setSelectedUsers(allUsers.map((option) => option.value));
    } else {
      setSelectedUsers([]);
    }
  };

  // Gestionnaire pour "Sélectionner tous les utilisateurs liés"
  const handleSelectAllLinkedUsers = (e) => {
    if (e.target.checked) {
      // Ajoute tous les utilisateurs liés à selectedUsers, en évitant les doublons
      setSelectedUsers((prevUsers) => [
        ...new Set([
          ...prevUsers,
          ...linkedUsers.map((option) => option.value),
        ]),
      ]);
    } else {
      // Retire les utilisateurs liés de selectedUsers
      setSelectedUsers((prevUsers) =>
        prevUsers.filter(
          (user) => !linkedUsers.map((option) => option.value).includes(user)
        )
      );
    }
  };

  // Gestionnaire pour "Sélectionner toutes les cabines"
  const handleSelectAllCabines = (e) => {
    if (e.target.checked) {
      setSelectedCabines(allCabines.map((option) => option.value));
    } else {
      setSelectedCabines([]);
    }
  };

  // Transformation des données reçues
  const transformedData = useMemo(
    () =>
      data.reduce((acc, curr) => {
        acc[curr.mname] = curr.totalValue;
        return acc;
      }, {}),
    [data]
  );

  // Fonction pour extraire et cumuler les valeurs spécifiques pour plusieurs mname
  const getValuesForMnames = (mnames, defaultValue = 0) => {
    return mnames.reduce(
      (acc, mname) => acc + (transformedData[mname] || 0),
      defaultValue
    );
  };

  const handleComparisonModeChange = (newMode) => {
    // Gérer le changement de mode de comparaison
    if (newMode !== comparisonMode) {
      setComparisonMode(newMode);
      if (newMode === "users") {
        setSelectedCabines([]);
        setComparisonData([]);
      } else if (newMode === "cabines") {
        setSelectedUsers([]);
        setComparisonData([]);
      } else {
        setComparisonData([]);
      }
      // Déclenche la récupération des données
      setTriggerFetch(!triggerFetch); // Basculer la valeur pour déclencher l'effet
    }
  };

  const [filtersOpen, setFiltersOpen] = useState(true);

  return (
    <div className={styles.page}>
      {/* ═══ HEADER ═══ */}
      <div className={styles.header}>
        <h2>{t("stats.title")}</h2>
        <div className={styles.actions}>
          <div className={styles.generalInfo}>
            <span className={styles.infoBadge}>
              <FiUsers /> {t("stats.users_count")} : <strong>{allUsers.length}</strong>
            </span>
            <span className={styles.infoBadge}>
              <FiMonitor /> {t("stats.cabins_count")} : <strong>{allCabines.length}</strong>
            </span>
          </div>
          {/* Active filters tags */}
          <div className={styles.activeFilters}>
            {startDate && endDate && (
              <span className={styles.filterTag}>
                {startDate.toLocaleDateString()} → {endDate.toLocaleDateString()}
              </span>
            )}
            {selectedUsers.length > 0 && (
              <span className={styles.filterTag}>
                {selectedUsers.length > 1 ? t("stats.user_count_label_plural", { count: selectedUsers.length }) : t("stats.user_count_label", { count: selectedUsers.length })}
              </span>
            )}
            {selectedCabines.length > 0 && (
              <span className={styles.filterTag}>
                {selectedCabines.length > 1 ? t("stats.cabin_count_label_plural", { count: selectedCabines.length }) : t("stats.cabin_count_label", { count: selectedCabines.length })}
              </span>
            )}
            {comparisonMode !== "none" && (
              <span className={styles.filterTag}>
                {t("stats.comparison_label", { mode: comparisonMode === "users" ? t("stats.comparison_users") : t("stats.comparison_cabins") })}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ═══ STAT CARDS ═══ */}
      <div className={styles.statsGrid}>
        {/* HS Started */}
        <div className={styles.statCard}>
          <div className={styles.statCardHeader}>
            <div>
              <div className={styles.statCardTitle}>HS Started</div>
              <div className={styles.statCardValue}>
                {getValuesForMnames(["HSStarted"])}
              </div>
            </div>
            <div className={`${styles.statCardIcon} ${styles.statCardIconBlue}`}>
              <FiPlay />
            </div>
          </div>
        </div>

        {/* Reports Generated */}
        <div className={styles.statCard}>
          <div className={styles.statCardHeader}>
            <div>
              <div className={styles.statCardTitle}>Reports Generated</div>
              <div className={styles.statCardValue}>
                {getValuesForMnames(["ReportsGenerated"])}
              </div>
            </div>
            <div className={`${styles.statCardIcon} ${styles.statCardIconGreen}`}>
              <FiFileText />
            </div>
          </div>
        </div>

        {/* Total Calibrations */}
        <div className={styles.statCard}>
          <div className={styles.statCardHeader}>
            <div>
              <div className={styles.statCardTitle}>Total Calibrations</div>
              <div className={styles.statCardValue}>
                {getValuesForMnames([
                  "SpeakersCalibrationCompleted",
                  "HeadphonesCalibrationCompleted",
                  "InsertsCalibrationCompleted",
                ])}
              </div>
            </div>
            <div className={`${styles.statCardIcon} ${styles.statCardIconOrange}`}>
              <FiVolume2 />
            </div>
          </div>
          <div className={styles.statCardDetails}>
            <div className={styles.statCardDetailRow}>
              <span>Speakers</span>
              <span>{getValuesForMnames(["SpeakersCalibrationCompleted"])}</span>
            </div>
            <div className={styles.statCardDetailRow}>
              <span>Headphones</span>
              <span>{getValuesForMnames(["HeadphonesCalibrationCompleted"])}</span>
            </div>
            <div className={styles.statCardDetailRow}>
              <span>Inserts</span>
              <span>{getValuesForMnames(["InsertsCalibrationCompleted"])}</span>
            </div>
          </div>
        </div>

        {/* Total Audiograms */}
        <div className={styles.statCard}>
          <div className={styles.statCardHeader}>
            <div>
              <div className={styles.statCardTitle}>Total Audiogrammes</div>
              <div className={styles.statCardValue}>
                {getValuesForMnames([
                  "TonalAudiogramSaved",
                  "VocalAudiogramSaved",
                  "TSTAudiogramSaved",
                  "LocalisationTestSaved",
                ])}
              </div>
            </div>
            <div className={`${styles.statCardIcon} ${styles.statCardIconPurple}`}>
              <FiActivity />
            </div>
          </div>
          <div className={styles.statCardDetails}>
            <div className={styles.statCardDetailRow}>
              <span>{t("stats.tonal")}</span>
              <span>{getValuesForMnames(["TonalAudiogramSaved"])}</span>
            </div>
            <div className={styles.statCardDetailRow}>
              <span>{t("stats.vocal")}</span>
              <span>{getValuesForMnames(["VocalAudiogramSaved"])}</span>
            </div>
            <div className={styles.statCardDetailRow}>
              <span>TST</span>
              <span>{getValuesForMnames(["TSTAudiogramSaved"])}</span>
            </div>
            <div className={styles.statCardDetailRow}>
              <span>{t("stats.localization")}</span>
              <span>{getValuesForMnames(["LocalisationTestSaved"])}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ FILTERS PANEL ═══ */}
      <div className={styles.filtersPanel}>
        <div
          className={styles.filtersPanelHeader}
          onClick={() => setFiltersOpen((v) => !v)}
        >
          <h3>
            <FiFilter /> {t("stats.data_filters")}
          </h3>
          <button type="button" className={styles.filtersPanelToggle}>
            {filtersOpen ? <FiChevronUp /> : <FiChevronDown />}
            {filtersOpen ? t("stats.collapse") : t("stats.expand")}
          </button>
        </div>

        {filtersOpen && (
          <div className={styles.filtersContent}>
            <div className={styles.filtersRow}>
              <div className={`${styles.filterGroup} ${styles.datePicker}`}>
                <label>{t("stats.period")}</label>
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    setStartDate(update[0]);
                    setEndDate(update[1]);
                  }}
                  dateFormat="dd/MM/yyyy"
                  showYearDropdown
                  showMonthDropdown
                  useShortMonthInDropdown
                  placeholderText={t("stats.select_date_range")}
                  isClearable={true}
                  monthsShown={1}
                />
              </div>

              <div className={styles.filterGroup}>
                <label>{t("stats.users")}</label>
                <Select
                  key={`user-select-${comparisonMode}`}
                  name="uid"
                  options={allUsers}
                  isMulti
                  onChange={handleUserChange}
                  value={allUsers.filter((user) =>
                    selectedUsers.includes(user.value)
                  )}
                  placeholder={t("common.select_user")}
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </div>

              <div className={styles.filterGroup}>
                <label>{t("stats.cabins")}</label>
                <Select
                  key={`cabine-select-${comparisonMode}`}
                  name="cabid"
                  options={allCabines}
                  isMulti
                  onChange={handleCabineChange}
                  value={allCabines.filter((cabine) =>
                    selectedCabines.includes(cabine.value)
                  )}
                  placeholder={t("common.select_cabin")}
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </div>
            </div>

            {/* Linked users row */}
            {linkedUsers.length > 0 && (
              <div className={styles.filtersRow}>
                <div className={styles.filterGroup}>
                  <label>{t("stats.linked_users")}</label>
                  <Select
                    name="linkedUid"
                    options={linkedUsers}
                    onChange={handleLinkedUserChange}
                    placeholder={t("common.select_linked_users")}
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    isMulti
                  />
                  {selectedUsers.length > 0 && (
                    <label className={styles.filterCheckbox}>
                      <input
                        type="checkbox"
                        onChange={handleSelectAllLinkedUsers}
                        checked={linkedUsers.every((user) =>
                          selectedUsers.includes(user.value)
                        )}
                      />
                      {t("common.select_all_linked_users")}
                    </label>
                  )}
                </div>
              </div>
            )}

            {/* Comparison mode */}
            <div className={styles.comparisonSection}>
              <h4>{t("stats.comparison_mode")}</h4>
              <div className={styles.radioGroup}>
                <button
                  type="button"
                  className={comparisonMode === "none" ? styles.radioBtnActive : styles.radioBtn}
                  onClick={() => handleComparisonModeChange("none")}
                >
                  {t("stats.none")}
                </button>
                <button
                  type="button"
                  className={comparisonMode === "users" ? styles.radioBtnActive : styles.radioBtn}
                  onClick={() => handleComparisonModeChange("users")}
                >
                  {t("stats.comparison_users")}
                </button>
                <button
                  type="button"
                  className={comparisonMode === "cabines" ? styles.radioBtnActive : styles.radioBtn}
                  onClick={() => handleComparisonModeChange("cabines")}
                >
                  {t("stats.comparison_cabins")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* ═══ CHARTS ═══ */}
      <div className={styles.chartsSection}>
        {comparisonMode === "cabines" || comparisonMode === "users" ? (
          <GlobalComparisonMetrics
            comparisonData={comparisonData}
            defaultMetrics={[defaultMetrics]}
            comparisonType={comparisonMode}
            setComparisonMode={setComparisonMode}
            usersMap={usersMap}
            cabinesMap={cabinesMap}
            timeInterval={timeInterval}
            setTimeInterval={setTimeInterval}
            selectedMetrics={selectedMetrics}
            setSelectedMetrics={setSelectedMetrics}
          />
        ) : (
          <GlobalSelectMetrics
            dataForChart={detailedData}
            defaultMetrics={[defaultMetrics]}
            timeInterval={timeInterval}
            setTimeInterval={setTimeInterval}
            selectedMetrics={selectedMetrics}
            setSelectedMetrics={setSelectedMetrics}
          />
        )}
      </div>
    </div>
  );
}

export default Statistics;
